import { ref } from "vue";
import apiClient from "@/services/api";
import { useLoadingService } from "@/services/LoadingService";
import { useErrorService } from "@/services/ErrorService";

export function usePreviewWorkflow(taskId) {
  const loadingSvc = useLoadingService(); // <-- use the store
  const isLoading = ref(false);
  const working_phase = ref("");
  const working_phase_percentage = ref(0);
  const error = ref(false);
  const error_message = ref("");
  const errorSvc = useErrorService();

  async function startPreviewAndPollStatus(
    formData,
    onOk,
    startCall = apiClient.getPreview,
  ) {
    const cancelPolling = { value: false };
    working_phase.value = "uploading";
    working_phase_percentage.value = 0;
    isLoading.value = true;

    loadingSvc.start(taskId, {
      title: "Uploading files...",
      progress: 0,
    });

    const handleProgress = (percent) => {
      working_phase_percentage.value = percent;
      loadingSvc.update(taskId, {
        title: "Uploading files...",
        progress: percent,
      });
    };

    try {
      await startCall(formData, handleProgress);
      pollStatus(cancelPolling, onOk);
    } catch (err) {
      error.value = true;
      working_phase.value = "error";
      error_message.value =
        err?.response?.data?.message ??
        (err?.message
          ? `Preview failed: ${err.message}`
          : "An unknown error occurred while processing preview.");
      isLoading.value = false;

      errorSvc.show("previewError", {
        title: "Preview Generation Failed",
        message:
          err?.response?.data?.message ??
          (err?.message
            ? `Preview failed: ${err.message}`
            : "An unknown error occurred while processing preview."),
        details: err?.stack,
      });
      loadingSvc.stop(taskId);
    }
  }

  async function pollStatus(cancelPolling, onOk) {
    working_phase.value = "Analyzing files";
    loadingSvc.update(taskId, { title: "Analyzing files…", progress: 0 });

    const tick = async () => {
      if (cancelPolling.value) return;

      try {
        const res = await apiClient.getPreviewStatus();
        const result = res.data;

        if (result.result === "OK") {
          onOk(result.message); // delegate data handling
          cancelPolling.value = true;
          working_phase.value = "done";
          isLoading.value = false;
          loadingSvc.stop(taskId);
          return;
        }

        if (result.result === "PROCESSING") {
          working_phase_percentage.value = result.message.progress;
          loadingSvc.update(taskId, {
            title: "Analyzing files…",
            progress: result.message.progress,
          });
        } else {
          cancelPolling.value = true;
          error.value = true;
          error_message.value = "Failed to get preview status";
          working_phase.value = "error";
          isLoading.value = false;
          loadingSvc.stop(taskId);
        }
      } catch (_e) {
        cancelPolling.value = true;
        error.value = true;
        error_message.value = "Failed to get preview status";
        working_phase.value = "error";
        isLoading.value = false;
        loadingSvc.stop(taskId);
      }

      setTimeout(tick, 2000);
    };

    tick();
  }

  return {
    isLoading,
    working_phase,
    working_phase_percentage,
    error,
    error_message,
    startPreviewAndPollStatus,
  };
}
